import Image from 'next/image'
import { getReadableImageUrl } from "@/lib/directus";
import Link from "next/link";
import { sanitizeHTML } from "@/lib/utils";

interface ContactUsHeroData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  desktop_image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  mobile_image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
}

export const fields = [
  "heading.text",
  "heading.title",

  "subheading.text",
  
  "button.label",
  "button.href",
  
  "desktop_image.alt",
  "desktop_image.image_file.image.id",
  "desktop_image.image_file.image.filename_download",
  
  "mobile_image.alt",
  "mobile_image.image_file.image.id",
  "mobile_image.image_file.image.filename_download",
] as const;

interface ContactUsHeroProps {
  data?: ContactUsHeroData;
}

export default function ContactUsHero({ data }: ContactUsHeroProps) {
  // Fallback images in case there's no data from Directus
  const desktopImage = data?.desktop_image?.image_file?.image?.id
    ? getReadableImageUrl(
        data.desktop_image.image_file.image.id,
        data.desktop_image.image_file.image.filename_download || ''
      )
    : "/images/contact-us-bg-desktop.png";

  const mobileImage = data?.mobile_image?.image_file?.image?.id
    ? getReadableImageUrl(
        data.mobile_image.image_file.image.id,
        data.mobile_image.image_file.image.filename_download || ''
      )
    : "/images/contact-us-bg-mobile.png";

  const desktopAlt = data?.desktop_image?.alt || "Contact Us";
  const mobileAlt = data?.mobile_image?.alt || "Contact Us";
  
  return (
    <section className="relative h-[19.9375rem] md:h-[42.0625rem] overflow-hidden">
      {/* Background Images */}
      <div className="absolute inset-0">
        <Image
          src={desktopImage}
          alt={desktopAlt}
          fill
          className="object-cover object-left md:block hidden w-full"
          sizes="(max-width: 768px) 0vw, 100vw"
          loading="lazy"
          quality={65}
        />
        <Image
          src={mobileImage}
          alt={mobileAlt}
          fill
          className="object-cover md:hidden block"
          sizes="(max-width: 768px) 100vw, 0vw"
          priority
          fetchPriority="high"
          quality={65}
        />
      </div>

      {/* Content Container */}
      <div className="relative w-full px-8 lg:px-16 md:h-full">
        <div className="md:h-full flex flex-col justify-start md:justify-end md:pb-32">
          {/* White semi-transparent container covering all content */}
          <div className="bg-white/65 backdrop-blur-sm rounded-xl p-8 md:p-12 mt-12 md:mt-0 max-w-3xl">
            <div className="space-y-8 md:space-y-12">
              {/* Title */}
              <h1 className="text-[2.5rem] font-essonnes font-light leading-tight md:text-[3.625rem] lg:text-[4rem] text-primary">
                {data?.heading?.text || data?.heading?.title || ""}
              </h1>

              {data?.subheading?.text && (
                <div className='text-[1.5rem] font-greycliff leading-7 text-primary mt-2' dangerouslySetInnerHTML={sanitizeHTML(data.subheading.text)} />
              )}

              {/* Button */}
              {data?.button && (
                <Link 
                  href={data.button.href || "#"} 
                  className="inline-flex h-[4.375rem] w-full md:w-fit items-center justify-center rounded-[3.125rem] px-9 text-xl font-normal tracking-tight text-white bg-primary hover:bg-primary/90 text-center"
                >
                  <span className="font-greycliff leading-7 text-center">{data.button.label || ""}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
} 